import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { socialAPI } from '../services/api.ts';

interface Post {
  id: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
    user_type?: string;
  };
  content: string;
  image?: string;
  media_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  likes_count: number;
  comments_count: number;
  created_at: string;
  liked_by_user: boolean;
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
    user_type: string;
  };
}

const ProfilePostsGrid: React.FC = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [commentsLoading, setCommentsLoading] = useState<boolean>(false);
  const [newComment, setNewComment] = useState<string>('');
  const [submittingComment, setSubmittingComment] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      fetchUserPosts();
    }
  }, [user]);

  const fetchUserPosts = async (): Promise<void> => {
    try {
      let response;

      // Check if the API method exists, if not use fallback
      if (socialAPI.getUserPosts) {
        response = await socialAPI.getUserPosts(user!.id);
      } else {
        // Fallback if endpoint doesn't exist yet
        response = { data: { posts: [] } };
      }

      // Filter posts that have media
      const postsWithMedia = (response.data.posts as Post[]).filter((post: Post) =>
        post.media_url || post.image
      );
      setPosts(postsWithMedia);
    } catch (error) {
      console.error('Failed to fetch user posts:', error);
      setPosts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const openPost = async (post: Post): Promise<void> => {
    setSelectedPost(post);
    setCommentsLoading(true);
    try {
      const response = await socialAPI.getComments(post.id);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const closePost = (): void => {
    setSelectedPost(null);
    setComments([]);
    setNewComment('');
    setSubmittingComment(false);
  };

  const handleSubmitComment = async (): Promise<void> => {
    if (!newComment.trim() || submittingComment || !selectedPost) return;

    setSubmittingComment(true);
    try {
      const response = await socialAPI.addComment(selectedPost.id, newComment.trim());

      // Add the new comment to the list
      const newCommentObj: Comment = {
        id: response.data.comment_id || Date.now().toString(),
        content: newComment.trim(),
        created_at: new Date().toISOString(),
        author: {
          id: user!.id,
          username: user!.username,
          first_name: user!.first_name,
          last_name: user!.last_name || '',
          profile_picture: user!.profile_picture,
          user_type: user!.role
        }
      };

      setComments([...comments, newCommentObj]);
      setNewComment('');

      // Update the post's comment count in the posts array
      setPosts(posts.map(p =>
        p.id === selectedPost.id
          ? { ...p, comments_count: p.comments_count + 1 }
          : p
      ));

      // Update the selected post's comment count
      setSelectedPost({
        ...selectedPost,
        comments_count: selectedPost.comments_count + 1
      });
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  const handleLike = async (postId: string): Promise<void> => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    try {
      if (post.liked_by_user) {
        await socialAPI.unlikePost(postId);
      } else {
        await socialAPI.likePost(postId);
      }

      // Update both the grid and modal
      const updatedPosts = posts.map(p =>
        p.id === postId
          ? {
            ...p,
            likes_count: post.liked_by_user ? p.likes_count - 1 : p.likes_count + 1,
            liked_by_user: !post.liked_by_user
          }
          : p
      );
      setPosts(updatedPosts);

      if (selectedPost && selectedPost.id === postId) {
        setSelectedPost(updatedPosts.find(p => p.id === postId) || null);
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  if (loading) {
    return (
      <div className="mt-8">
        <div className="grid grid-cols-3 gap-1">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="aspect-square bg-gray-200 animate-pulse rounded-sm"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-8">
        {posts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No posts yet</h3>
            <p className="text-gray-500">Share photos and videos to see them on your profile.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1">
            {posts.map((post) => (
              <div
                key={post.id}
                onClick={() => openPost(post)}
                className="relative aspect-square cursor-pointer group overflow-hidden rounded-sm"
              >
                {post.media_type === 'video' ? (
                  <div className="relative">
                    <img
                      src={post.thumbnail_url || post.media_url?.replace('.mp4', '.jpg')}
                      alt="Post content"
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute top-2 right-2">
                      <svg className="h-4 w-4 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <img
                    src={post.media_url || post.image}
                    alt="Post content"
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-25 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-6 text-white">
                    <div className="flex items-center space-x-1">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                      <span className="font-semibold">{post.likes_count}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span className="font-semibold">{post.comments_count}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Post Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex">
            {/* Media Section */}
            <div className="flex-1 bg-black flex items-center justify-center">
              {selectedPost.media_type === 'video' ? (
                <video
                  src={selectedPost.media_url}
                  controls
                  className="max-w-full max-h-full"
                />
              ) : (
                <img
                  src={selectedPost.media_url || selectedPost.image}
                  alt="Post content"
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>

            {/* Content Section */}
            <div className="w-96 flex flex-col">
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-300 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {selectedPost.author.first_name[0]}{selectedPost.author.last_name[0]}
                  </div>
                  <div>
                    <span className="font-semibold text-sm">{selectedPost.author.first_name} {selectedPost.author.last_name}</span>
                    {selectedPost.author.user_type === 'trainer' && (
                      <span className="ml-2 bg-green-400 text-gray-800 text-xs px-2 py-0.5 rounded-full">
                        Trainer
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closePost}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Post Content */}
              <div className="p-4 border-b">
                <p className="text-sm">{selectedPost.content}</p>
                <span className="text-xs text-gray-500 mt-2 block">
                  {formatTimeAgo(selectedPost.created_at)}
                </span>
              </div>

              {/* Comments */}
              <div className="flex-1 p-4 overflow-y-auto">
                {commentsLoading ? (
                  <div className="text-center text-gray-500">Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div className="text-center text-gray-500">No comments yet</div>
                ) : (
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-300 to-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {comment.author.first_name[0]}
                        </div>
                        <div className="flex-1 text-sm">
                          <span className="font-semibold">{comment.author.first_name}</span>
                          {' '}
                          <span>{comment.content}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 border-t">
                <div className="flex space-x-4 mb-2">
                  <button
                    onClick={() => handleLike(selectedPost.id)}
                    className={`flex items-center space-x-1 ${selectedPost.liked_by_user ? 'text-red-500' : 'text-gray-700 hover:text-red-500'
                      }`}
                  >
                    <svg className="h-6 w-6" fill={selectedPost.liked_by_user ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  <button className="text-gray-700 hover:text-gray-900">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                </div>
                <div className="text-sm font-semibold mb-3">{selectedPost.likes_count} likes</div>

                {/* Add Comment Section */}
                <div className="border-t pt-3">
                  <div className="flex space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-300 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Add a comment..."
                        className="w-full p-2 border border-gray-200 rounded-lg bg-white/80 resize-none focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm"
                        rows={2}
                        disabled={submittingComment}
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={handleSubmitComment}
                          disabled={!newComment.trim() || submittingComment}
                          className="bg-green-400 text-gray-800 px-3 py-1 rounded-full hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          {submittingComment ? 'Posting...' : 'Post'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilePostsGrid;